import { Box, Flex, Grid, GridItem, Icon, Text } from '@chakra-ui/react';
import { CaseMap } from '@llx/common';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import eyeIcon from '@/assets/images/eye.svg?react';
import caseSelectorBg from '@/assets/images/liveMeetingBackground.svg?url';
import { useLoggedInUserAuthContext } from '@/components/AuthTokenContextProvider/UserAuthContext';
import AnamneseItem, { AnamneseItemWrapper } from '@/components/CaseSlide/items/AnamneseItem';
import { getAnamenseItemFromCaseMap } from '@/features/caseCluster/helpers/getAnamneseItem';
import { SimpleContentModal } from '@/ui/SimpleContentModal';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import MeetingTestingModeWarning from './MeetingTestingModeWarning';

type Props = {
  patientCaseIds: string[];
  caseMap: CaseMap | undefined;
  onCaseSelected: (caseID: string) => void;
  shouldOpenAnamneseOnClick: boolean;
  showTestingModeWarning: boolean;
  showHostIsPresentingMessage: boolean;
};

function CaseSelectorView({
  patientCaseIds,
  caseMap,
  onCaseSelected,
  shouldOpenAnamneseOnClick,
  showTestingModeWarning,
  showHostIsPresentingMessage,
}: Props) {
  const { t } = useTranslation();
  const numItems = patientCaseIds?.length;
  const [currentModal, setCurrentModal] = useState<string | undefined>();
  const { isAdmin } = useLoggedInUserAuthContext();

  const numCols = useMemo(() => {
    if (numItems == 4) return 2;
    if (numItems == 3 && window.innerWidth < window.innerHeight) return 1;
    return numItems > 3 ? 3 : numItems;
  }, [numItems]);

  const numRows = Math.ceil(numItems / numCols);
  const hasMultipleRows = numRows > 1;

  const onCaseSelectorItemClick = (id: string) => {
    onCaseSelected(id);
    if (shouldOpenAnamneseOnClick) {
      setCurrentModal(id);
    }
  };

  return (
    <Flex
      bgImage={caseSelectorBg}
      backgroundSize="cover"
      bgColor="livelinx.purple400"
      h="100%"
      w="full"
      overflow="auto"
      alignItems="center"
      justifyContent="center"
      position="relative"
    >
      {showHostIsPresentingMessage && (
        <Box
          position="absolute"
          right="0"
          top="0"
          color="white"
          textAlign="right"
          pt="10px"
          pr="20px"
          display="flex"
          alignItems="center"
          gap="10px"
        >
          <Icon as={eyeIcon} boxSize="25px"></Icon>
          <Text>
            {t('home.cases.caseSelector.hostIsPresenting', {
              num: numItems,
            })}
          </Text>
        </Box>
      )}

      {showTestingModeWarning && <MeetingTestingModeWarning isAdmin={isAdmin} />}

      <Grid
        templateRows={`repeat(${numRows}, 1fr)`}
        templateColumns={`repeat(${numCols}, 1fr)`}
        gap="5vh"
        padding="5vh"
        height="100%"
        justifyContent="center"
        alignItems="center"
      >
        {caseMap &&
          patientCaseIds?.map((id, caseIndex) => {
            //NOTE: This means that the caseIDList is not in sync with the caseMap, we haven't loaded the content yet
            if (!caseMap[id]) {
              console.warn(
                'We have not loaded the case ',
                id,
                ' yet so we are not displaying it yet'
              );
              console.warn('caseMap', caseMap);
              return;
            }
            const overrideItem = caseMap[id].caseSelectorItemOverride;

            //if there is an override
            if (overrideItem) {
              return (
                <GridItem
                  key={id}
                  w="full"
                  h={hasMultipleRows ? '100%' : 'auto'}
                  overflow="hidden"
                  rowSpan={1}
                  colSpan={1}
                >
                  <AnamneseItemWrapper
                    onClick={() => {
                      onCaseSelectorItemClick(id);
                    }}
                  >
                    {overrideItem.title}
                  </AnamneseItemWrapper>
                </GridItem>
              );
            }

            const anamneseItem = getAnamenseItemFromCaseMap(caseMap, id);
            // case no Anamese found
            if (!anamneseItem) {
              console.error(`No anamneseItem found for case: ${id}`);
              captureSentry({
                title: 'No anamneseItem found for case',
                detail: { caseID: id, caseMap, caseIDList: patientCaseIds },
              });
              return (
                <GridItem key={id} overflow="hidden" rowSpan={1} colSpan={1} color="red">
                  {t('home.cases.caseSelector.noAnamneseItemFound')}
                </GridItem>
              );
            }

            return (
              <GridItem
                key={id}
                w="full"
                h={hasMultipleRows ? '100%' : 'auto'}
                overflow="hidden"
                rowSpan={1}
                colSpan={1}
              >
                <SimpleContentModal
                  isOpen={id === currentModal}
                  onClose={() => setCurrentModal(undefined)}
                  content={
                    <AnamneseItem
                      title={`CASUS ${caseIndex + 1}`}
                      isWomanCase={anamneseItem.gender === 'woman'}
                      subtitle={anamneseItem.title}
                      descriptionList={anamneseItem.descriptionList}
                    />
                  }
                />
                <AnamneseItem
                  title={`CASUS ${caseIndex + 1}`}
                  isWomanCase={anamneseItem.gender === 'woman'}
                  subtitle={anamneseItem.title}
                  descriptionList={anamneseItem.descriptionList}
                  isGridRowItem={hasMultipleRows}
                  onClick={() => {
                    onCaseSelectorItemClick(id);
                  }}
                />
              </GridItem>
            );
          })}
      </Grid>
    </Flex>
  );
}

export default CaseSelectorView;
